import * as R from 'ramda';
import { useEffect, useRef } from 'react';

import { Modals, PlayerErrorMessages } from '@/constants';
import { log } from '@/services';
import {
  playerActions,
  playerThunks,
  selectIsPlaying,
  selectIsTrackLoaded,
  selectPersistedPlayerVolumePreference,
  selectPlayerSeekToPosition,
  uiActions,
  useAppDispatch,
  useAppSelector,
} from '@/store';

interface AudioVideoPlayerProps {
  playerStreamUrl?: string;
}

const LOCK_KEY = 'cpa.player.transition';

export const AudioVideoPlayer = ({
  playerStreamUrl,
}: AudioVideoPlayerProps) => {
  const dispatch = useAppDispatch();

  const trackPlayerRef = useRef<HTMLAudioElement>(null);

  const isPlayerPlaying = useAppSelector(selectIsPlaying);
  const volume = useAppSelector(selectPersistedPlayerVolumePreference);
  const isTrackLoaded = useAppSelector(selectIsTrackLoaded);
  const seekToPosition = useAppSelector(selectPlayerSeekToPosition);

  useEffect(() => {
    if (!isTrackLoaded || !seekToPosition || !trackPlayerRef.current) return;

    try {
      trackPlayerRef.current.currentTime = seekToPosition;
    } catch (error) {
      dispatch(playerActions.clearSeekToPosition());
      log(error);
    }
  }, [dispatch, isTrackLoaded, seekToPosition]);

  useEffect(() => {
    if (!isTrackLoaded) return;

    navigator.locks.request(LOCK_KEY, async () => {
      if (
        !trackPlayerRef.current ||
        trackPlayerRef.current.paused === !isPlayerPlaying
      )
        return;

      if (isPlayerPlaying) {
        await trackPlayerRef.current?.play();
      } else {
        trackPlayerRef.current?.pause();
      }
    });
  }, [dispatch, isPlayerPlaying, isTrackLoaded]);

  useEffect(() => {
    if (!trackPlayerRef.current) return;

    trackPlayerRef.current.volume = volume / 100;
  }, [volume]);

  const onReady = () => {
    dispatch(playerActions.setTrackLoaded(true));
  };

  const onBuffer = () => {
    dispatch(playerActions.setBuffering(true));
  };

  const onBufferEnd = () => {
    dispatch(playerActions.setBuffering(false));
  };

  const onSeek = () => {
    const position = trackPlayerRef.current?.currentTime;

    if (R.isNil(position)) return;
    dispatch(
      playerThunks.seekToPosition.fulfilled(undefined, '', {
        position,
      }),
    );
  };

  const onProgress = () => {
    const playedSeconds = trackPlayerRef.current?.currentTime;
    const buffered = trackPlayerRef.current?.buffered;

    try {
      if (!isTrackLoaded || R.isNil(playedSeconds)) return;

      const progress = {
        position: Math.floor(playedSeconds),
        buffered,
      };

      dispatch(
        playerThunks.updatePlayerProgress.pending(
          '',
          { progress },
          { progress },
        ),
      );
    } catch (error) {
      log(error);
    }
  };

  const onEnded = () => {
    dispatch(playerThunks.chapterEnded());
  };

  const onError = () => {
    const error = trackPlayerRef.current?.error;
    if (!error) return;

    if (
      PlayerErrorMessages.BROWSER_AUTOPLAY_PROHIBITED.some(errorMessage =>
        error.message?.includes(errorMessage),
      )
    ) {
      dispatch(playerThunks.pausePlayer());
      dispatch(
        uiActions.setActiveModal({ name: Modals.BROWSER_AUTOPLAY_PROHIBITED }),
      );
      return;
    }

    log(
      new Error('AudioVideoPlayer library error handler', {
        cause: new Error(error?.message),
      }),
      {
        playerStreamUrl,
      },
      error,
    );
  };

  const onContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  return (
    <audio
      ref={trackPlayerRef}
      src={playerStreamUrl}
      onEnded={onEnded}
      onProgress={onProgress}
      onSeeked={onSeek}
      onError={onError}
      onCanPlay={onReady}
      onWaiting={onBuffer}
      onPlaying={onBufferEnd}
      controls={false}
      onContextMenu={onContextMenu}
    />
  );
};
